import { CompositionStage, useComposition, animate, Easing, clamp, lerp } from "@/components/hero/stage";

/* Prevention hero — "the years arrive".
   Travel: the years scroll towards you and the screenings land on them as they pass.
   Risk: one hundred people appear, and the frightening headline shrinks to its real size.
   Kept: the years you buy back, stacked as a short honest ledger. */

const W = 800, H = 660;
const BLUE = "#4C9EDA";
const DEEP = "#2E6FA3";
const INK = "#122534";
const FONT = "Outfit, Poppins, system-ui, sans-serif";
const SCENES = [
  { name: "Travel", dur: 7 },
  { name: "Risk", dur: 5 },
  { name: "Kept", dur: 4.5 },
];

const YEARS = [
  { age: 38, t: "Blood pressure, lipids, glucose", now: true },
  { age: 45, t: "Bowel screening begins" },
  { age: 50, t: "Shingles vaccine · PSA conversation" },
  { age: 55, t: "Lung screening, if you ever smoked" },
  { age: 65, t: "Aortic scan, once, ten minutes" },
  { age: 70, t: "Bone density · pneumococcal" },
];

const fade = (T, a, b, d = 0.6) => Math.min(clamp((T - a) / d, 0, 1), clamp((b - T) / d, 0, 1));
const LEDGER = [
  { t: "Stop smoking", y: 10 },
  { t: "Treat blood pressure", y: 4 },
  { t: "Move most days", y: 4 },
  { t: "Attend the screening", y: 2 },
];

function Years({ accent = BLUE }) {
  const { T: rawT, CUES: rawCues, authoredTotal, compact } = useComposition();
  const CUES = { Travel: 0, Risk: 7, Kept: 12, ...rawCues };
  const T = Number.isFinite(rawT) ? rawT : 0;
  const total = authoredTotal || 16.5;
  const A = (o) => animate(o)(T);

  const travel = fade(T, -0.4, CUES.Risk + 0.4, 0.7);
  const risk = fade(T, CUES.Risk - 0.2, CUES.Kept + 0.4, 0.7);
  const kept = fade(T, CUES.Kept - 0.2, total + 0.5, 0.7);

  /* the road: years slide from the bottom up towards a vanishing point */
  const march = A({ from: 0, to: 1, start: 0.5, end: CUES.Risk - 0.6, ease: Easing.linear });

  /* risk: 4 in 100 baseline, headline says +30% */
  const rel = A({ from: 0, to: 30, start: CUES.Risk + 0.9, end: CUES.Risk + 2.1, ease: Easing.easeOutCubic });
  const base = 4;
  const after = base * (1 + rel / 100);
  const shrink = A({ from: 0, to: 1, start: CUES.Risk + 2.3, end: CUES.Risk + 3.4, ease: Easing.easeInOutCubic });

  return (
    <div style={{ position: "absolute", inset: 0, fontFamily: FONT }}>
      {/* ---------------------------------------------------- the road */}
      <div style={{ position: "absolute", inset: 0, opacity: travel }}>
        <div style={{ position: "absolute", left: 40, top: 30, font: "500 12.5px/1 " + FONT, letterSpacing: "0.2em", color: "rgba(18,37,52,0.42)" }}>
          THE YEARS ARRIVE WHETHER YOU PLAN FOR THEM OR NOT
        </div>
        {YEARS.map((y, i) => {
          /* each year rises through the frame, on a loop offset by index */
          const p = (march * 1.15 + 1 - i * 0.17) % 1.15;
          if (p < 0 || p > 1) return null;
          const depth = Easing.easeInOutSine(p);
          const top = lerp(560, 96, depth);
          const s = lerp(0.72, 1.06, depth);
          const inset = lerp(150, 44, depth);
          const op = Math.min(1, p * 4) * Math.min(1, (1 - p) * 5.5);
          return (
            <div key={y.age} style={{
              position: "absolute", left: inset, right: inset, top,
              opacity: op, transform: "scale(" + s + ")", transformOrigin: "50% 50%",
            }}>
              <div style={{
                display: "flex", alignItems: "center", gap: 18, padding: "16px 22px", borderRadius: 20,
                background: y.now ? "linear-gradient(135deg, " + accent + ", " + DEEP + ")" : "rgba(255,255,255,0.9)",
                border: "1px solid " + (y.now ? "transparent" : "rgba(18,37,52,0.08)"),
                boxShadow: y.now ? "0 26px 54px -26px rgba(46,111,163,0.75)" : "0 22px 44px -30px rgba(18,37,52,0.6)",
              }}>
                <span style={{
                  font: "500 30px/1 " + FONT, fontVariantNumeric: "tabular-nums",
                  color: y.now ? "#fff" : DEEP, minWidth: 52,
                }}>{y.age}</span>
                <span style={{ font: "400 15.5px/1.3 " + FONT, color: y.now ? "rgba(255,255,255,0.9)" : "rgba(18,37,52,0.7)" }}>{y.t}</span>
              </div>
            </div>
          );
        })}
        {/* the horizon the years come from */}
        <div style={{ position: "absolute", left: 0, right: 0, top: 82, height: 1, background: "linear-gradient(90deg, transparent, rgba(46,111,163,0.35), transparent)" }} />
        <div style={{ position: "absolute", left: 0, right: 0, bottom: 26, textAlign: "center", font: "500 12px/1 " + FONT, letterSpacing: "0.2em", color: "rgba(18,37,52,0.32)" }}>
          AND EVERY ONE OF THEM HAS SOMETHING WORTH DOING IN IT
        </div>
      </div>

      {/* ---------------------------------------------------- the risk reader */}
      <div style={{ position: "absolute", left: 44, top: 74, width: 700, opacity: risk }}>
        <div style={{ font: "500 12.5px/1 " + FONT, letterSpacing: "0.2em", color: "rgba(18,37,52,0.42)" }}>THE HEADLINE, AND THEN THE PEOPLE</div>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 14, marginTop: 12 }}>
          <span style={{ font: "500 62px/0.95 " + FONT, color: "#C4553F", fontVariantNumeric: "tabular-nums" }}>+{Math.round(rel)}%</span>
          <span style={{ font: "400 16px/1.4 " + FONT, color: "rgba(18,37,52,0.55)", paddingBottom: 10, maxWidth: 260 }}>
            is what the article says, and it is true
          </span>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(20, 1fr)", gap: 6, marginTop: 26, width: 620 }}>
          {Array.from({ length: 100 }, (_, i) => {
            const isBase = i < Math.round(base);
            const isExtra = !isBase && i < Math.round(after * (1 - shrink) + after * shrink);
            return (
              <span key={i} style={{
                aspectRatio: "1", borderRadius: 5,
                background: isBase ? DEEP : isExtra ? "#E4886F" : "rgba(18,37,52,0.09)",
                transform: "scale(" + (isExtra ? lerp(1.35, 1, shrink) : 1) + ")",
              }} />
            );
          })}
        </div>

        <div style={{ display: "flex", gap: 34, marginTop: 26, opacity: shrink }}>
          {[[base, "in 100 before"], [after.toFixed(1), "in 100 after"], ["+" + (after - base).toFixed(1), "the whole difference"]].map(([a, b], i) => (
            <div key={b}>
              <div style={{ font: "500 30px/1 " + FONT, color: i === 2 ? INK : i === 0 ? DEEP : "#C4553F", fontVariantNumeric: "tabular-nums" }}>{a}</div>
              <div style={{ font: "400 12.5px/1.4 " + FONT, color: "rgba(18,37,52,0.45)", marginTop: 6 }}>{b}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ---------------------------------------------------- the ledger */}
      <div style={{ position: "absolute", left: 44, top: 92, width: 620, opacity: kept }}>
        <div style={{ font: "500 12.5px/1 " + FONT, letterSpacing: "0.2em", color: "rgba(18,37,52,0.42)" }}>WHAT THE BORING THINGS ACTUALLY BUY BACK</div>
        {LEDGER.map((l, i) => {
          const at = CUES.Kept + 0.4 + i * 0.35;
          const p = A({ from: 0, to: 1, start: at, end: at + 0.8, ease: Easing.easeOutCubic });
          return (
            <div key={l.t} style={{ marginTop: i ? 20 : 26, opacity: p }}>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
                <span style={{ font: "400 17px/1 " + FONT, color: INK }}>{l.t}</span>
                <span style={{ font: "500 15px/1 " + FONT, color: DEEP, fontVariantNumeric: "tabular-nums" }}>up to {l.y} years</span>
              </div>
              <div style={{ height: 12, borderRadius: 6, background: "rgba(18,37,52,0.08)", marginTop: 9, overflow: "hidden" }}>
                <div style={{ width: (l.y / 10) * 100 * p + "%", height: 12, borderRadius: 6, background: "linear-gradient(90deg, " + accent + ", " + DEEP + ")" }} />
              </div>
            </div>
          );
        })}
        {!compact && (
          <div style={{ font: "400 14.5px/1.5 " + FONT, color: "rgba(18,37,52,0.55)", marginTop: 30, maxWidth: 520 }}>
            No scans you do not need, no supplements, no scare tactics. Three answers and a calendar that runs decades forward.
          </div>
        )}
      </div>

      <div style={{ position: "absolute", right: 4, bottom: 6, display: "flex", gap: 7 }}>
        {SCENES.map((s, i) => {
          const on = (i === 0 && T < CUES.Risk) || (i === 1 && T >= CUES.Risk && T < CUES.Kept) || (i === 2 && T >= CUES.Kept);
          return <span key={s.name} style={{ width: on ? 22 : 8, height: 8, borderRadius: 4, background: on ? DEEP : "rgba(18,37,52,0.16)", transition: "width 0.4s" }} />;
        })}
      </div>
    </div>
  );
}

export default function PreventionHero({ accent = BLUE }) {
  return (
    <CompositionStage width={W} height={H} scenes={SCENES} minScale={0.56} maxHeight={440}>
      <Years accent={accent} />
    </CompositionStage>
  );
}
